import { useState, useEffect, useCallback } from 'react';
import { automunityAPI } from '../lib/api';

export type AccessRole = 'admin' | 'developer' | 'viewer';

export interface PagePermission {
  page: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface AuthUser {
  user_id: number;
  full_name: string;
  email: string;
  access_role: AccessRole;
  permissions: PagePermission[];
}

/**
 * Local permission map for role simulation.
 * Mirrors the access_permission seed data so admins can preview other roles client-side.
 */
const LOCAL_PERMISSIONS: Record<AccessRole, Record<string, { can_view: boolean; can_create: boolean; can_edit: boolean; can_delete: boolean }>> = {
  admin: {
    dashboard:    { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    automations:  { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    requests:     { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    changelog:    { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    deployments:  { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    cleanup:      { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    monitor:      { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    integrations: { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    reports:      { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    chat:         { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    users:        { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    settings:     { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
  },
  developer: {
    dashboard:    { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    automations:  { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    requests:     { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    changelog:    { can_view: true,  can_create: true,  can_edit: true,  can_delete: false },
    deployments:  { can_view: true,  can_create: true,  can_edit: true,  can_delete: true },
    cleanup:      { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    monitor:      { can_view: true,  can_create: true,  can_edit: true,  can_delete: false },
    integrations: { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    reports:      { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    chat:         { can_view: true,  can_create: true,  can_edit: false, can_delete: false },
    users:        { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    settings:     { can_view: false, can_create: false, can_edit: false, can_delete: false },
  },
  viewer: {
    dashboard:    { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    automations:  { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    requests:     { can_view: true,  can_create: true,  can_edit: false, can_delete: false },
    changelog:    { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    deployments:  { can_view: false, can_create: false, can_edit: false, can_delete: false },
    cleanup:      { can_view: false, can_create: false, can_edit: false, can_delete: false },
    monitor:      { can_view: false, can_create: false, can_edit: false, can_delete: false },
    integrations: { can_view: false, can_create: false, can_edit: false, can_delete: false },
    reports:      { can_view: true,  can_create: false, can_edit: false, can_delete: false },
    chat:         { can_view: true,  can_create: true,  can_edit: false, can_delete: false },
    users:        { can_view: false, can_create: false, can_edit: false, can_delete: false },
    settings:     { can_view: false, can_create: false, can_edit: false, can_delete: false },
  }
};

export function useAuth() {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleOverride, setRoleOverride] = useState<AccessRole | null>(null);

  useEffect(() => {
    loadAuthUser();
  }, []);

  const loadAuthUser = async () => {
    try {
      setLoading(true);
      const response = await automunityAPI.getMyAccess();
      const data = await response.body.json();
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      
      // records can be: a JSON string, an array, or the response itself is the record
      let record;
      if (parsed?.records) {
        const records = typeof parsed.records === 'string' ? JSON.parse(parsed.records) : parsed.records;
        record = Array.isArray(records) ? records[0] : records;
      } else if (parsed?.access_role) {
        record = parsed;
      } else {
        record = parsed;
      }
      
      setAuthUser({
        user_id: record.user_id,
        full_name: record.full_name,
        email: record.email,
        access_role: record.access_role || 'viewer',
        permissions: record.permissions || []
      });
      setError(null);
    } catch (err) {
      console.error('Failed to load access role:', err);
      setAuthUser(null);
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  // The effective role: override (if set by admin) or real role
  const effectiveRole: AccessRole = roleOverride || authUser?.access_role || 'viewer';

  const hasPermission = useCallback((page: string, action: 'can_view' | 'can_create' | 'can_edit' | 'can_delete'): boolean => {
    if (!authUser) return false;
    const role = roleOverride || authUser.access_role;
    if (role === 'admin') return true;
    // Use local permission map (works for both real role and overridden role)
    const perms = LOCAL_PERMISSIONS[role]?.[page];
    return perms ? !!perms[action] : false;
  }, [authUser, roleOverride]);

  const canView = useCallback((page: string): boolean => {
    return hasPermission(page, 'can_view');
  }, [hasPermission]);

  const canCreate = useCallback((page: string): boolean => {
    return hasPermission(page, 'can_create');
  }, [hasPermission]);

  const canEdit = useCallback((page: string): boolean => {
    return hasPermission(page, 'can_edit');
  }, [hasPermission]);

  const canDelete = useCallback((page: string): boolean => {
    return hasPermission(page, 'can_delete');
  }, [hasPermission]);

  const canEditOwn = useCallback((page: string, itemOwnerId?: number): boolean => {
    if (!authUser) return false;
    const role = roleOverride || authUser.access_role;
    if (role === 'admin') return true;
    if (role === 'developer') {
      if (itemOwnerId === authUser.user_id) return true;
      return hasPermission(page, 'can_edit');
    }
    if (role === 'viewer') {
      if (page === 'requests' && itemOwnerId === authUser.user_id) return true;
    }
    return false;
  }, [authUser, roleOverride, hasPermission]);

  const canDeleteOwn = useCallback((page: string, itemOwnerId?: number): boolean => {
    if (!authUser) return false;
    const role = roleOverride || authUser.access_role;
    if (role === 'admin') return true;
    if (role === 'developer') {
      if (itemOwnerId === authUser.user_id) return true;
      return hasPermission(page, 'can_delete');
    }
    return false;
  }, [authUser, roleOverride, hasPermission]);

  // These reflect the REAL role (not override) — used for gating admin-only features like the switcher
  const isAdmin = authUser?.access_role === 'admin';
  const isDeveloper = effectiveRole === 'developer';
  const isViewer = effectiveRole === 'viewer';

  return {
    authUser,
    loading,
    error,
    effectiveRole,
    roleOverride,
    setRoleOverride,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canEditOwn,
    canDeleteOwn,
    isAdmin,
    isDeveloper,
    isViewer,
    refresh: loadAuthUser
  };
}
